import { Landmark, Package, Wrench } from "lucide-react"
import type { ComponentType } from "react"
import { Insignia, type Tono } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"

/**
 * Panel de rubros del expediente.
 *
 * ⚠ **Un rubro de tipo `IMPUESTO` no muestra campo de proveedor**, y eso no es una
 * preferencia de diseño: es la misma regla que impone
 * `ck_expediente_items_impuesto_sin_proveedor` en la base. Un impuesto se le paga al
 * fisco, no a un proveedor. Mostrar el campo invitaría a rellenarlo y el servidor
 * rechazaría el formulario con un error que el operador no se explica.
 *
 * ⚠ **Todo importe lleva `tabular-nums` y su signo explícito.** Sin `tabular-nums`
 * las cifras se desalinean al paginar (regla 10). Y el signo se escribe porque el
 * color nunca es el único portador de significado: un egreso en rojo y un ingreso en
 * verde son la misma columna para quien no distingue esos dos colores.
 *
 * Los colores salen de las variables de `estilos/globales.css`, como en v1. Una
 * clase de color de Tailwind no responde al tema oscuro; un token sí.
 */

export type TipoRubro = "GASTO" | "SERVICIO" | "IMPUESTO"
export type EstadoPago = "BORRADOR" | "PENDIENTE" | "PARCIAL" | "PAGADO"

export type Rubro = {
  id: number
  tipo: TipoRubro
  concepto: string
  proveedorNombre: string | null
  cantidad: string
  precioUnitario: string
  moneda: "USD" | "BS"
  estadoPago: EstadoPago
}

const ICONO_DE: Record<TipoRubro, ComponentType<{ size?: number; "aria-hidden"?: boolean }>> = {
  GASTO: Package,
  SERVICIO: Wrench,
  IMPUESTO: Landmark,
}

/**
 * Si este tipo de rubro admite proveedor.
 *
 * Se expone como función y no como un `if` dentro del JSX para que la regla se
 * pueda leer —y probar— en un solo sitio. Es el reflejo exacto del `CHECK` de la
 * tabla, y las dos tienen que decir lo mismo.
 */
export function admiteProveedor(tipo: TipoRubro): boolean {
  return tipo !== "IMPUESTO"
}

/** Cantidad por precio, con dos decimales. El mismo cálculo que el backend. */
export function totalDelRubro(rubro: Pick<Rubro, "cantidad" | "precioUnitario">): string {
  return (Number(rubro.cantidad) * Number(rubro.precioUnitario)).toFixed(2)
}

/**
 * Importe con signo explícito y separadores.
 *
 * `signo` no es decorativo: WCAG 1.4.1 exige que el color no sea el único portador
 * de significado, y en una tabla de dinero la diferencia entre cobrar y pagar es
 * exactamente lo que no puede depender de si el usuario distingue rojo de verde.
 */
export function formatearImporte(
  valor: string,
  moneda: "USD" | "BS",
  signo: "+" | "-" | "",
): string {
  const numero = Number(valor).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `${signo}${numero} ${moneda}`
}

const TONO_ESTADO: Record<EstadoPago, Tono> = {
  BORRADOR: "neutro",
  PENDIENTE: "aviso",
  PARCIAL: "info",
  PAGADO: "exito",
}

type Props = {
  rubros: Rubro[]
  /**
   * Abre el pago de un rubro. Ausente en las vistas de sólo lectura.
   *
   * ⚠ Un rubro `PAGADO` no ofrece el botón — no lo ofrece deshabilitado: no lo
   * ofrece. La misma regla que `TablaSaldos`, y por el mismo motivo: pulsarlo
   * devolvería un `MONTO_EXCEDE_SALDO` que no explica nada, porque no hay nada
   * pendiente que pagar.
   */
  onPagar?: (rubroId: number) => void
}

export function PanelRubros({ rubros, onPagar }: Props) {
  if (rubros.length === 0) {
    return (
      <p className="superficie border-dashed p-6 text-center" style={{ color: "var(--fg-muted)" }}>
        Este expediente todavía no tiene rubros.
      </p>
    )
  }

  return (
    <Tabla etiqueta="Rubros del expediente">
      <Cabecera>
        <Th>Concepto</Th>
        <Th>Proveedor</Th>
        <Th derecha>Cantidad</Th>
        <Th derecha>Precio</Th>
        <Th derecha>Total</Th>
        <Th>Estado</Th>
        {onPagar && <Th />}
      </Cabecera>
      <Cuerpo>
        {rubros.map((rubro) => {
          const Icono = ICONO_DE[rubro.tipo]
          return (
            <Fila key={rubro.id}>
              <Td>
                <span className="flex items-center gap-2">
                  <Icono size={16} aria-hidden={true} />
                  <span>{rubro.concepto}</span>
                  <span className="sr-only">{rubro.tipo}</span>
                </span>
              </Td>
              <Td>
                {admiteProveedor(rubro.tipo) ? (
                  <span data-testid={`proveedor-${rubro.id}`}>{rubro.proveedorNombre ?? "—"}</span>
                ) : (
                  // Un impuesto se le paga al fisco. No hay campo que rellenar.
                  <span style={{ color: "var(--fg-muted)" }}>Fisco</span>
                )}
              </Td>
              <Td derecha numero>
                {rubro.cantidad}
              </Td>
              <Td derecha numero>
                {formatearImporte(rubro.precioUnitario, rubro.moneda, "")}
              </Td>
              <Td derecha numero className="font-medium">
                {formatearImporte(totalDelRubro(rubro), rubro.moneda, "-")}
              </Td>
              <Td>
                <Insignia tono={TONO_ESTADO[rubro.estadoPago]}>{rubro.estadoPago}</Insignia>
              </Td>
              {onPagar && (
                <Td derecha>
                  {rubro.estadoPago !== "PAGADO" && (
                    <button
                      type="button"
                      data-testid={`pagar-${rubro.id}`}
                      onClick={() => onPagar(rubro.id)}
                      className="rounded-[var(--radius-control)] border px-2 py-1 text-xs"
                      style={{ borderColor: "var(--border)" }}
                    >
                      Pagar
                    </button>
                  )}
                </Td>
              )}
            </Fila>
          )
        })}
      </Cuerpo>
    </Tabla>
  )
}
